// src/pages/Notifications/ShopNotification.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  TextField,
  Button,
  Chip,
  Paper,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  Snackbar,
  Alert,
  CircularProgress,
  Stack,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tabs,
  Tab,
  MenuItem,
  Select,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import CampaignIcon from '@mui/icons-material/Campaign';
import HistoryIcon from '@mui/icons-material/History';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { jwtDecode } from 'jwt-decode';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
import { format } from 'date-fns';

const ShopNotification = () => {
  // Navigation Tab State (0 = Send Form, 1 = History Log)
  const [activeTab, setActiveTab] = useState(0);

  // Form states
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [notificationType, setNotificationType] = useState('info'); // 'info' | 'ad' | 'alert' | 'offer'
  const [priority, setPriority] = useState('normal'); // 'normal' | 'high'

  const [actionUrl, setActionUrl] = useState('');
  const [actionButtonText, setActionButtonText] = useState('View');

  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const { can } = usePermissions();

  // Selection mode states
  const [selectionType, setSelectionType] = useState('district'); // 'district' | 'village' | 'pincode'
  const [isMultiple, setIsMultiple] = useState(true); // true = Multiple, false = Single

  // Target value states
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [singleDistrict, setSingleDistrict] = useState(null);

  const [selectedVillages, setSelectedVillages] = useState([]);
  const [singleVillage, setSingleVillage] = useState(null);

  const [currentPincode, setCurrentPincode] = useState('');
  const [pincodeList, setPincodeList] = useState([]);

  // Available Data lists
  const [districts, setDistricts] = useState([]);
  const [villages, setVillages] = useState([]);

  // History Log States
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [historyTypeFilter, setHistoryTypeFilter] = useState('ALL');

  // Pop-Up Dialog states
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  // Error and Notification states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [errors, setErrors] = useState({});

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchDistricts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/districts`);
      const sorted = (response.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setDistricts(sorted);
    } catch (error) {
      showSnackbar('Error fetching districts', 'error');
    }
  }, []);

  const fetchVillages = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/taluks`);
      const villageMap = new Map();
      (response.data || []).forEach(v => {
        if (v && v.name && !villageMap.has(v.name)) {
          villageMap.set(v.name, v);
        }
      });
      const uniqueVillages = Array.from(villageMap.values()).sort((a, b) => a.name.localeCompare(b.name));
      setVillages(uniqueVillages);
    } catch (error) {
      console.error('Error fetching villages:', error);
      showSnackbar('Error fetching villages', 'error');
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const res = await axios.get(`${API_BASE_URL}/notifications?limit=50`);
      if (res.status === 200 && res.data?.notifications) {
        const merchantItems = (res.data.notifications || []).filter(n => 
          Array.isArray(n.targetUserTypes) && n.targetUserTypes.includes('vendor')
        );
        setHistoryList(merchantItems);
      }
    } catch (error) {
      console.error('Error fetching notification history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUserId(decodedToken.userId);
        fetchDistricts();
        fetchVillages();
        fetchHistory();
      } catch (error) {
        console.error('Error decoding token:', error);
        showSnackbar('Authentication error', 'error');
      }
    }
  }, [fetchDistricts, fetchVillages, fetchHistory]);

  const handleDeleteNotification = async (notificationId) => {
    if (!can('notifications.shop.delete')) return;
    if (!window.confirm('Are you sure you want to delete this notification record?')) return;
    try {
      const token = sessionStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSnackbar('Notification deleted successfully', 'success');
      fetchHistory();
    } catch (err) {
      showSnackbar('Failed to delete notification', 'error');
    }
  };

  // Pincode Handlers
  const handleAddPincode = () => {
    if (currentPincode.length !== 6) {
      setErrors({ ...errors, pincode: 'Pincode must be exactly 6 digits' });
      return;
    }
    if (!isMultiple && pincodeList.length >= 1) {
      setErrors({ ...errors, pincode: 'Single mode allows only 1 pincode' });
      return;
    }
    if (pincodeList.includes(currentPincode)) {
      setErrors({ ...errors, pincode: 'Pincode already added' });
      return;
    }
    setPincodeList([...pincodeList, currentPincode]);
    setCurrentPincode('');
    setErrors({ ...errors, pincode: null });
  };

  const handleRemovePincode = (pincodeToRemove) => {
    setPincodeList(pincodeList.filter(p => p !== pincodeToRemove));
  };

  const handlePincodeInputChange = (event) => {
    const value = event.target.value.replace(/\D/g, '').substring(0, 6);
    setCurrentPincode(value);
    if (errors.pincode) {
      setErrors({ ...errors, pincode: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!title.trim()) {
      newErrors.title = 'Notification Title is required';
    }

    if (!body.trim()) {
      newErrors.body = 'Notification Body is required';
    }

    if (selectionType === 'district') {
      if (isMultiple && selectedDistricts.length === 0) {
        newErrors.districts = 'Please select at least one district';
      }
      if (!isMultiple && !singleDistrict) {
        newErrors.districts = 'Please select a district';
      }
    }

    if (selectionType === 'village') {
      if (isMultiple && selectedVillages.length === 0) {
        newErrors.villages = 'Please select at least one village';
      }
      if (!isMultiple && !singleVillage) {
        newErrors.villages = 'Please select a village';
      }
    }

    if (selectionType === 'pincode') {
      if (pincodeList.length === 0) {
        newErrors.pincodes = 'Please add at least one pincode';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Open Confirmation Pop-Up Modal
  const handleOpenConfirmDialog = () => {
    if (!can('notifications.shop.send')) return;
    if (validateForm()) {
      setConfirmDialogOpen(true);
    }
  };

  // Execute Send Notification
  const handleConfirmSend = async () => {
    if (!can('notifications.shop.send')) return;
    setConfirmDialogOpen(false);
    setLoading(true);

    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        showSnackbar('Authentication token not found', 'error');
        setLoading(false);
        return;
      }

      const payload = {
        title,
        body,
        notificationType,
        priority,
        actionUrl: actionUrl.trim(),
        actionButtonText: actionButtonText.trim() || 'View',
        targetCategory: selectionType,
        targetUserTypes: ['vendor'],
        scheduledFor: new Date().toISOString()
      };

      if (selectionType === 'district') {
        payload.districts = isMultiple
          ? selectedDistricts.map(d => d._id)
          : (singleDistrict ? [singleDistrict._id] : []);
      } else if (selectionType === 'village') {
        payload.taluks = isMultiple
          ? selectedVillages.map(v => v._id)
          : (singleVillage ? [singleVillage._id] : []);
      } else {
        payload.pincodes = pincodeList.map(Number);
      }

      const res = await axios.post(`${API_BASE_URL}/notifications/${userId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.status === 200 || res.status === 201) {
        setSuccessMsg('Merchant Notification sent successfully!');
        setSuccessDialogOpen(true);
        fetchHistory();
        
        // Reset form
        setTitle('');
        setBody('');
        setActionUrl('');
        setActionButtonText('View');
        setSelectedDistricts([]);
        setSingleDistrict(null);
        setSelectedVillages([]);
        setSingleVillage(null);
        setPincodeList([]);
        setCurrentPincode('');
      } else {
        showSnackbar('Failed to send notification', 'error');
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Error sending notification';
      showSnackbar(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Clean Notification Type Badges without emojis
  const renderTypeChip = (type) => {
    switch (type) {
      case 'ad':
        return <Chip icon={<CampaignIcon />} label="AD / BANNER" color="warning" size="small" sx={{ fontWeight: 'bold' }} />;
      case 'alert':
        return <Chip icon={<AnnouncementIcon />} label="IMPORTANT ALERT" color="error" size="small" sx={{ fontWeight: 'bold' }} />;
      case 'offer':
        return <Chip icon={<LocalOfferIcon />} label="SPECIAL OFFER" color="secondary" size="small" sx={{ fontWeight: 'bold' }} />;
      default:
        return <Chip icon={<InfoIcon />} label="GENERAL INFO" color="info" size="small" sx={{ fontWeight: 'bold' }} />;
    }
  };

  // Filtered History List
  const filteredHistory = historyList.filter(item => {
    if (historyTypeFilter !== 'ALL' && item.notificationType !== historyTypeFilter) return false;
    if (historySearch.trim()) {
      const term = historySearch.toLowerCase();
      const t = (item.title || '').toLowerCase();
      const b = (item.body || '').toLowerCase();
      return t.includes(term) || b.includes(term);
    }
    return true;
  });

  const getTargetLocationDisplay = () => {
    if (selectionType === 'district') {
      const names = isMultiple ? selectedDistricts.map(d => d.name) : (singleDistrict ? [singleDistrict.name] : []);
      return `${names.join(', ')} (${isMultiple ? 'Multiple' : 'Single'} District)`;
    } else if (selectionType === 'village') {
      const names = isMultiple ? selectedVillages.map(v => v.name) : (singleVillage ? [singleVillage.name] : []);
      return `${names.join(', ')} (${isMultiple ? 'Multiple' : 'Single'} Village)`;
    } else {
      return `${pincodeList.join(', ')} (${isMultiple ? 'Multiple' : 'Single'} Pincode)`;
    }
  };

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br /><br />
      <Box sx={{ width: '100%', p: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
          Merchant Notification & Ads Management
        </Typography>

        {/* Top Navigation Tabs */}
        <Paper elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab icon={<CampaignIcon />} label="Send Notification & Ads" sx={{ fontWeight: 'bold', py: 1.5 }} />
            <Tab icon={<HistoryIcon />} label={`Notification History (${historyList.length})`} sx={{ fontWeight: 'bold', py: 1.5 }} />
          </Tabs>
        </Paper>

        {/* TAB 0: Send Notification Form */}
        {activeTab === 0 && (
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
            <Grid container spacing={3}>
              {/* Target Category Selection */}
              <Grid item xs={12} md={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
                    1. Select Target Location
                  </FormLabel>
                  <RadioGroup
                    row
                    value={selectionType}
                    onChange={(e) => {
                      setSelectionType(e.target.value);
                      setErrors({});
                    }}
                  >
                    <FormControlLabel value="district" control={<Radio />} label="DISTRICT WISE" />
                    <FormControlLabel value="village" control={<Radio />} label="VILLAGE WISE" />
                    <FormControlLabel value="pincode" control={<Radio />} label="PINCODE WISE" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* Single vs Multiple Selection Mode */}
              <Grid item xs={12} md={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
                    2. Select Mode (Single / Multiple Search)
                  </FormLabel>
                  <RadioGroup
                    row
                    value={isMultiple ? 'multiple' : 'single'}
                    onChange={(e) => {
                      const multi = e.target.value === 'multiple';
                      setIsMultiple(multi);
                      setSelectedDistricts([]);
                      setSingleDistrict(null);
                      setSelectedVillages([]);
                      setSingleVillage(null);
                      setPincodeList([]);
                    }}
                  >
                    <FormControlLabel value="single" control={<Radio />} label="Single Selection" />
                    <FormControlLabel value="multiple" control={<Radio />} label="Multiple Selection" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* DISTRICT WISE Selection */}
              {selectionType === 'district' && (
                <Grid item xs={12}>
                  {isMultiple ? (
                    <Autocomplete
                      multiple
                      options={districts}
                      getOptionLabel={(option) => (option ? option.name || '' : '')}
                      isOptionEqualToValue={(option, value) => option && value && (option._id === value._id || option.name === value.name)}
                      value={selectedDistricts}
                      onChange={(event, newValue) => setSelectedDistricts(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Search & Select Multiple Districts"
                          placeholder="Type district name..."
                          error={!!errors.districts}
                          helperText={errors.districts}
                        />
                      )}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            label={option ? option.name : ''}
                            {...getTagProps({ index })}
                            key={option._id || index}
                            color="primary"
                            variant="outlined"
                          />
                        ))
                      }
                    />
                  ) : (
                    <Autocomplete
                      options={districts}
                      getOptionLabel={(option) => (option ? option.name || '' : '')}
                      isOptionEqualToValue={(option, value) => option && value && (option._id === value._id || option.name === value.name)}
                      value={singleDistrict}
                      onChange={(event, newValue) => setSingleDistrict(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Search & Select Single District"
                          placeholder="Type district name..."
                          error={!!errors.districts}
                          helperText={errors.districts}
                        />
                      )}
                    />
                  )}
                </Grid>
              )}

              {/* VILLAGE WISE Selection */}
              {selectionType === 'village' && (
                <Grid item xs={12}>
                  {isMultiple ? (
                    <Autocomplete
                      multiple
                      options={villages}
                      getOptionLabel={(option) => (option ? option.name || '' : '')}
                      isOptionEqualToValue={(option, value) => option && value && (option._id === value._id || option.name === value.name)}
                      value={selectedVillages}
                      onChange={(event, newValue) => setSelectedVillages(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Search & Select Multiple Villages"
                          placeholder="Type village name..."
                          error={!!errors.villages}
                          helperText={errors.villages}
                        />
                      )}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            label={option ? option.name : ''}
                            {...getTagProps({ index })}
                            key={option._id || index}
                            color="secondary"
                            variant="outlined"
                          />
                        ))
                      }
                    />
                  ) : (
                    <Autocomplete
                      options={villages}
                      getOptionLabel={(option) => (option ? option.name || '' : '')}
                      isOptionEqualToValue={(option, value) => option && value && (option._id === value._id || option.name === value.name)}
                      value={singleVillage}
                      onChange={(event, newValue) => setSingleVillage(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Search & Select Single Village"
                          placeholder="Type village name..."
                          error={!!errors.villages}
                          helperText={errors.villages}
                        />
                      )}
                    />
                  )}
                </Grid>
              )}

              {/* PINCODE WISE Selection */}
              {selectionType === 'pincode' && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <TextField
                      label="Enter Pincode (6 digits)"
                      value={currentPincode}
                      onChange={handlePincodeInputChange}
                      error={!!errors.pincode || !!errors.pincodes}
                      helperText={errors.pincode || errors.pincodes}
                      size="small"
                      sx={{ flex: 1 }}
                      inputProps={{ maxLength: 6 }}
                    />
                    <Button variant="contained" color="primary" onClick={handleAddPincode} sx={{ height: 40 }}>
                      Add Pincode
                    </Button>
                  </Box>

                  {pincodeList.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                      {pincodeList.map((pin) => (
                        <Chip
                          key={pin}
                          label={pin}
                          onDelete={() => handleRemovePincode(pin)}
                          color="info"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  )}
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* 3. Notification Type & Priority without emojis */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Notification / Ad Category</InputLabel>
                  <Select
                    value={notificationType}
                    label="Notification / Ad Category"
                    onChange={(e) => setNotificationType(e.target.value)}
                  >
                    <MenuItem value="info">General Information</MenuItem>
                    <MenuItem value="ad">Advertisement / Banner Ad</MenuItem>
                    <MenuItem value="alert">Important Alert / Announcement</MenuItem>
                    <MenuItem value="offer">Special Offer / Reward Banner</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority Level</InputLabel>
                  <Select
                    value={priority}
                    label="Priority Level"
                    onChange={(e) => setPriority(e.target.value)}
                  >
                    <MenuItem value="normal">Normal Priority</MenuItem>
                    <MenuItem value="high">High Priority (Urgent Banner)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Notification Content Fields */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notification Title *"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  error={!!errors.title}
                  helperText={errors.title}
                  placeholder="e.g. New Merchant Commission Rate Update"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notification Description / Message *"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  error={!!errors.body}
                  helperText={errors.body}
                  placeholder="Enter detailed message or promotional copy..."
                />
              </Grid>

              {/* Target Action Link & Button Label */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Action URL / App Deep Link (Optional)"
                  value={actionUrl}
                  onChange={(e) => setActionUrl(e.target.value)}
                  placeholder="https://mythrismartbazaar.com/merchant-notice or qpay://merchant/notice"
                  helperText="URL or deep link opened when merchant taps the banner"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Action Button Text"
                  value={actionButtonText}
                  onChange={(e) => setActionButtonText(e.target.value)}
                  placeholder="e.g. View Notice, Claim Offer, Open Dashboard"
                />
              </Grid>

              {/* Submit Button */}
              {can('notifications.shop.send') && (
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    onClick={handleOpenConfirmDialog}
                    disabled={loading}
                    sx={{ py: 1.5, px: 4, fontWeight: 'bold' }}
                  >
                    Send Merchant Notification / Ad
                  </Button>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {/* TAB 1: History Log */}
        {activeTab === 1 && (
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3, alignItems: 'center' }}>
              <Box sx={{ flex: 1, minWidth: 250, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SearchIcon color="action" />
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="Search notification history by title or body..."
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                />
              </Box>

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Type Filter</InputLabel>
                <Select
                  value={historyTypeFilter}
                  label="Type Filter"
                  onChange={(e) => setHistoryTypeFilter(e.target.value)}
                >
                  <MenuItem value="ALL">All Categories</MenuItem>
                  <MenuItem value="ad">Ad / Banner</MenuItem>
                  <MenuItem value="alert">Alert / Announcement</MenuItem>
                  <MenuItem value="offer">Offer / Reward</MenuItem>
                  <MenuItem value="info">General Info</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: 'primary.main' }}>
                    <TableRow>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date & Time</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Title & Description</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Action Link</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredHistory.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          No notification records found in history.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredHistory.map((item) => (
                        <TableRow key={item._id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {format(new Date(item.createdAt || item.scheduledFor), 'dd-MM-yyyy HH:mm')}
                          </TableCell>
                          <TableCell>{renderTypeChip(item.notificationType)}</TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{item.title}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                              {item.body}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.actionUrl ? (
                              <Typography variant="caption" color="primary" noWrap display="block">
                                {item.actionUrl}
                              </Typography>
                            ) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.status || 'sent'}
                              color={item.status === 'sent' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {can('notifications.shop.delete') && (
                              <Tooltip title="Delete Notification Record">
                                <IconButton color="error" size="small" onClick={() => handleDeleteNotification(item._id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {/* Confirmation Dialog Modal */}
        <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
            Confirm Send Merchant Notification / Ad
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
              <Typography variant="body1">
                <strong>Category:</strong> {renderTypeChip(notificationType)}
              </Typography>
              <Typography variant="body1">
                <strong>Title:</strong> {title}
              </Typography>
              <Typography variant="body1">
                <strong>Message:</strong> {body}
              </Typography>
              {actionUrl && (
                <Typography variant="body1">
                  <strong>Action Link:</strong> {actionUrl} ({actionButtonText})
                </Typography>
              )}
              <Typography variant="body1">
                <strong>Target Locations:</strong> {getTargetLocationDisplay()}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setConfirmDialogOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleConfirmSend} variant="contained" color="primary">
              Confirm & Send Now
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success Dialog Modal */}
        <Dialog open={successDialogOpen} onClose={() => setSuccessDialogOpen(false)}>
          <DialogContent sx={{ textAlign: 'center', p: 4 }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
              Success!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {successMsg}
            </Typography>
            <Button variant="contained" color="primary" onClick={() => setSuccessDialogOpen(false)}>
              OK
            </Button>
          </DialogContent>
        </Dialog>

        {/* Snackbar Notification */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default ShopNotification;